'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { memoizedUrlParse } from '@hms-dbmi-bgm/shared-portal-components/es/components/util';
import StaticPage from './StaticPage';


const dynamicImports = {};


export default class StatisticsPageView extends React.PureComponent {

    static defaultProps = {
        'defaultTab' : 'submissions'
    };

    static viewOptions = {
        'submissions' : {
            'title' : "Data Submissions Statistics",
            'icon' : 'upload fas',
            'tip' : "View statistics related to submission and release of Files",
            'shouldReaggregate' : function(pastProps, nextProps, pastState, nextState){
                if(!pastState || !nextState) return false;
                // Compare object references
                if (pastState.cumulativeSum !== nextState.cumulativeSum) return true;
            }
        },
        'usage' : {
            'title' : "Portal Usage Statistics",
            'icon' : 'users fas',
            'tip' : "View statistics related to usage of the SMaHT Data Portal",
            'shouldReaggregate' : function(pastProps, nextProps, pastState, nextState){
                // Compare object references
                if ((pastProps.countBy !== nextProps.countBy) || (pastState.cumulativeSum !== nextState.cumulativeSum)) return true;
            }
        }
    };

    constructor(props){
        super(props);
        this.maybeUpdateCurrentTabFromHref = this.maybeUpdateCurrentTabFromHref.bind(this);
        this.renderSubmissionsSection = this.renderSubmissionsSection.bind(this);
        this.renderUsageSection = this.renderUsageSection.bind(this);
        this.renderTopMenu = this.renderTopMenu.bind(this);
        this.state = { 'currentTab' : props.defaultTab };
    }

    componentDidMount(){
        const { onComplete } = this.props;

        this.maybeUpdateCurrentTabFromHref();

        if (!dynamicImports.UsageStatsView) {
            setTimeout(()=>{
                // Load in stats page components/code separately (code-splitting)

                // FOR DEVELOPMENT:
                // include `/* webpackMode: "eager" */`, since `npm run dev-quick` won't re-compile dynamic imports correctly.
                // "statistics-page-components" is aliased to './components/StatisticsPageViewBody' in webpack.config.js
                import(
                    /* webpackChunkName: "statistics-page-components" */
                    "statistics-page-components"
                ).then((loadedModule) => {
                    _.extend(dynamicImports, loadedModule);
                    this.setState({ mounted: true });
                });

            });
        } else if (onComplete && typeof onComplete === 'function') {
            onComplete();
        }
    }

    componentDidUpdate(pastProps){
        const { href } = this.props;
        if (href !== pastProps.href){
            this.maybeUpdateCurrentTabFromHref();
        }
    }

    maybeUpdateCurrentTabFromHref(){
        const { href } = this.props;
        this.setState(function({ currentTab }){
            const hrefParts = href && memoizedUrlParse(href);
            const hash = hrefParts && hrefParts.hash && hrefParts.hash.replace('#', '');
            if (hash && hash !== currentTab && hash.charAt(0) !== '!'){
                if (typeof StatisticsPageView.viewOptions[hash] !== 'undefined'){
                    return { 'currentTab' : hash };
                }
            }
        });
    }

    renderSubmissionsSection(){
        const { shouldReaggregate } = StatisticsPageView.viewOptions.submissions;
        // GroupByController is on outside here because SubmissionStatsViewController detects if props.currentGroupBy has changed in orded to re-fetch aggs.
        const groupByOptions = {
            'data_generation_summary.submission_centers'    : <span><i className="icon icon-fw fas icon-university me-1" />Submission Center</span>,
            'dataset'                                       : <span><i className="icon icon-fw fas icon-database me-1" />Sample</span>,
            // 'data_generation_summary.data_type'          : <span><i className="icon icon-fw fas icon-cubes me-1"/>Data Type</span>,
            // 'data_generation_summary.data_category'      : <span><i className="icon icon-fw fas icon-sitemap me-1"/>Data Category</span>,
            'data_generation_summary.assays'                : <span><i className="icon icon-fw fas icon-vial me-1" />Experimental Assay</span>,
            'file_format.display_title'                     : <span><i className="icon icon-fw fas icon-file-alt me-1" />File Format</span>,
            // 'data_generation_summary.sequencing_center'  : <span><i className="icon icon-fw fas icon-dna me-1"/>Generated By</span>,
            'data_generation_summary.sequencing_platforms'  : <span><i className="icon icon-fw fas icon-clipboard-list me-1" />Sequencing Platform</span>,
            // 'software.display_title'                     : <span><i className="icon icon-fw fas icon-code me-1"/>Software</span>,
        };
        const initialGroupBy = 'data_generation_summary.submission_centers';
        
        const singleSelectFilterOptions = {
            'all'            : <span>All</span>,
            'all_without_dac': <span>All (w/o HMS-DAC)</span>,
            'gcc_only'       : <span>GCC-only</span>,
            'ttd_only'       : <span>TTD-only</span>
        }
        const initialSingleSelectFilter = 'all_without_dac';

        const dateRangeOptions = {
            'all'           : <span>All</span>,
            'thismonth'     : <span>This Month</span>,
            'previousmonth' : <span>Previous Month</span>,
            'last3months'   : <span>Last 3 Months</span>,
            'last6months'   : <span>Last 6 Months</span>,
            'last12months'  : <span>Last 12 Months</span>,
            'thisyear'      : <span>This Year</span>,
            'previousyear'  : <span>Previous Year</span>,
            'custom'        : <span>Custom</span>
        };
        const initialDateRangePreset = 'last12months';

        const dateHistogramIntervalOptions = {
            'daily'     : <span>Daily</span>,
            'weekly'    : <span>Weekly</span>,
            'monthly'   : <span>Monthly</span>,
        }
        const initialDateHistogramInterval = 'weekly';

        const groupByControllerProps = {
            groupByOptions, initialGroupBy,
            singleSelectFilterOptions, initialSingleSelectFilter,
            dateRangeOptions, initialDateRangePreset,
            dateHistogramIntervalOptions, initialDateHistogramInterval
        };

        return (
            <dynamicImports.GroupByController {...groupByControllerProps}>
                <dynamicImports.SubmissionStatsViewController {..._.pick(this.props, 'session', 'windowWidth')}>
                    <dynamicImports.StatsChartViewAggregator {...{ shouldReaggregate }} aggregationsToChartData={dynamicImports.submissionsAggsToChartData}>
                        <dynamicImports.SubmissionsStatsView />
                    </dynamicImports.StatsChartViewAggregator>
                </dynamicImports.SubmissionStatsViewController>
            </dynamicImports.GroupByController>
        );
    }

    renderUsageSection(){
        const { shouldReaggregate } = StatisticsPageView.viewOptions.usage;
        const groupByOptions = {
            'daily:30': <span>Previous 30 Days</span>,
            'daily:60': <span>Previous 60 Days</span>,
            'monthly:6': <span>Previous 6 Months</span>,
            'monthly:12': <span>Previous 12 Months</span>,
            'monthly:18': <span>Previous 18 Months</span>,
            'monthly:All': <span>All</span>
        };
        const dataKeys = _.keys(dynamicImports.usageAggsToChartData || {});
        const initialChartToggles = {
            'chart': dataKeys.reduce((acc, key) => { acc[key] = true; return acc; }, {}),
            'table': dataKeys.reduce((acc, key) => { acc[key] = true; return acc; }, {}),
            'expanded': dataKeys.reduce((acc, key) => { acc[key] = false; return acc; }, {})
        }
        // override
        // initialChartToggles.table['top_file_set_downloads'] = true;
        initialChartToggles.chart['top_file_set_downloads'] = false;
        // initialChartToggles.table['top_file_set_downloads_volume'] = true;
        initialChartToggles.chart['top_file_set_downloads_volume'] = false;
        return (
            <dynamicImports.GroupByController groupByOptions={groupByOptions} initialGroupBy="daily:60">
                <dynamicImports.UsageStatsViewController {..._.pick(this.props, 'session', 'windowWidth', 'href', 'schemas')}>
                    <dynamicImports.StatsChartViewAggregator {...{ shouldReaggregate }} aggregationsToChartData={dynamicImports.usageAggsToChartData} initialChartToggles={initialChartToggles}>
                        <dynamicImports.UsageStatsView />
                    </dynamicImports.StatsChartViewAggregator>
                </dynamicImports.UsageStatsViewController>
            </dynamicImports.GroupByController>
        );
    }

    renderTopMenu(){
        const { currentTab } = this.state;
        const submissionsObj = StatisticsPageView.viewOptions.submissions;
        const usageObj = StatisticsPageView.viewOptions.usage;

        return (
            <div className="chart-section-control-wrapper row">
                <div className="col-sm-6">
                    <a className={"select-section-btn" + (currentTab === 'submissions' ? ' active' : '')}
                        href="#submissions" data-tip={currentTab === 'submissions' ? null : submissionsObj.tip} data-target-offset={110}>
                        { submissionsObj.icon ? <i className={"me-07 text-medium icon icon-fw icon-" + submissionsObj.icon}/> : null }
                        { submissionsObj.title }
                    </a>
                </div>
                <div className="col-sm-6">
                    <a className={"select-section-btn" + (currentTab === 'usage' ? ' active' : '')}
                        href="#usage" data-tip={currentTab === 'usage' ? null : usageObj.tip} data-target-offset={100}>
                        { usageObj.icon ? <i className={"me-07 text-medium icon icon-fw icon-" + usageObj.icon}/> : null }
                        { usageObj.title }
                    </a>
                </div>
            </div>
        );
    }

    render(){
        const { currentTab, mounted = false } = this.state;
        let body = null;
        if (mounted) {
            body = currentTab === 'usage' ? this.renderUsageSection() : this.renderSubmissionsSection();
        }
        return (
            <StaticPage.Wrapper>
                { this.renderTopMenu() }
                <hr/>
                { body }
            </StaticPage.Wrapper>
        );
    }
}